import { Component, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { SessionService } from '../../core/session.service';
import { I18nService } from '../../core/i18n.service';
import { ProfileWizardAnswers } from '../../core/models';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { safeExternalUrl, safeSameOriginOrHttpImageUrl } from '../../core/url-security';

@Component({ selector: 'app-account', standalone: true, imports: [FormsModule, LoadingSpinner], templateUrl: './account.html', styleUrl: './account.scss' })
export class Account {
  username = ''; firstName = ''; lastName = ''; email = ''; avatar = ''; avatarImportEnabled = true; uploadStatusKey = ''; isUploadingPicture = false; isDeletingPicture = false;
  avatarLoadFailed = false;
  isExportingPrivacyData = false; isDeletingAccount = false; deleteConfirmation = '';
  isSavingProfile = false; isGeneratingProfileSummary = false;
  private isEditingProfileSummary = false;
  profileSummary = '';
  showProfileCompletionModal = false;
  private profileCompletionModalDismissed = false;
  showProfileWizard = false;
  profileWizardStep = 0;
  profileWizard: ProfileWizardAnswers = this.emptyProfileWizard();
  private localAvatarPreviewUrl = '';
  private readonly pendingPictureDeletionStorageKey = 'phdcompass.pendingProfilePictureDeletionUrl';
  diplomaDraft = '';
  private diplomaSuggestionOffset = 0;
  private chipSuggestionOffsets: Record<keyof Omit<ProfileWizardAnswers, 'diplomas'>, number> = {
    favoriteTopics: 0,
    dislikedTasks: 0,
    methods: 0,
    values: 0,
    targetEnvironment: 0,
    achievements: 0,
    motivation: 0,
    extraNotes: 0
  };

  constructor(public session: SessionService, public i18n: I18nService, private route: ActivatedRoute) {
    effect(() => this.syncFromCurrentUser());
  }

  private syncFromCurrentUser(): void {
    const user = this.session.currentUser();
    if (user) { this.username = user.username; this.firstName = user.firstName; this.lastName = user.lastName; this.email = user.email; this.avatarImportEnabled = user.avatarImportEnabled ?? true; if (!this.isUploadingPicture && !this.isDeletingPicture) this.avatar = user.avatar ?? ''; this.avatarLoadFailed = false; this.syncPendingPictureDeletionNotice(); if (!this.isEditingProfileSummary && !this.isSavingProfile && !this.isGeneratingProfileSummary) this.profileSummary = user.profileSummary ?? ''; this.profileWizard = { ...this.emptyProfileWizard(), ...user.profileWizard }; if (this.isOrcidCompletion && !this.profileCompletionModalDismissed) this.showProfileCompletionModal = true; }
  }
  get uploadStatus(): string { return this.uploadStatusKey ? this.i18n.t(this.uploadStatusKey) : ''; }
  private get pendingDeletedAvatarUrl(): string { return this.readPendingDeletedAvatarUrl(); }
  get isPictureDeletionStillPending(): boolean { return Boolean(this.pendingDeletedAvatarUrl && this.avatar === this.pendingDeletedAvatarUrl); }
  get displayedAvatar(): string { return this.avatarLoadFailed || this.isPictureDeletionStillPending ? '' : safeSameOriginOrHttpImageUrl(this.avatar); }
  handleAvatarLoadError(): void { this.avatarLoadFailed = true; }
  get avatarImportProviderLabel(): string { return this.session.currentUser()?.avatarImportProvider === 'facebook' ? 'Facebook' : this.session.currentUser()?.avatarImportProvider === 'google' ? 'Google' : this.session.currentUser()?.avatarImportProvider === 'x' ? 'X' : this.i18n.t('account.loginProvider'); }
  get isProfileReady(): boolean { return this.session.currentUser() !== null; }
  get isOrcidCompletion(): boolean { return this.session.needsRequiredProfileCompletion() || this.route.snapshot.queryParamMap.has('complete'); }
  get isXCompletion(): boolean { const user = this.session.currentUser(); return this.route.snapshot.queryParamMap.get('complete') === 'x' || user?.loginProvider === 'x' || user?.avatarImportProvider === 'x'; }
  get profileCompletionProviderLabel(): string { return this.isXCompletion ? 'X' : 'ORCID'; }
  get profileCompletionTitle(): string { return this.isXCompletion ? this.i18n.t('account.oauthCompletionTitle', { provider: 'X' }) : this.i18n.t('account.orcidCompletionTitle'); }
  get profileCompletionHelp(): string { return this.isXCompletion ? this.i18n.t('account.oauthCompletionHelp', { provider: 'X' }) : this.i18n.t('account.orcidCompletionHelp'); }
  get profileFetchedInfoHelp(): string { return this.isXCompletion ? this.i18n.t('account.oauthFetchedInfoHelp') : this.i18n.t('account.orcidFetchedInfoHelp'); }
  get profileConfirmButtonLabel(): string { return this.isXCompletion ? this.i18n.t('account.confirmOauthProfile') : this.i18n.t('account.confirmOrcidProfile'); }
  get showEmailVerificationNotice(): boolean { const user = this.session.currentUser(); return Boolean(user?.email?.trim() && user.emailVerified === false); }
  get canEditEmail(): boolean { const user = this.session.currentUser(); return Boolean((user?.orcidId || user?.loginProvider === 'x' || user?.avatarImportProvider === 'x') && !user?.email?.trim()); }
  get orcidUri(): string { return this.session.currentUser()?.orcidUri || (this.session.currentUser()?.orcidId ? `https://orcid.org/${this.session.currentUser()?.orcidId}` : ''); }
  get safeOrcidUri(): string { return safeExternalUrl(this.orcidUri); }
  get initials(): string { return `${this.firstName.trim().charAt(0) || '?'}${this.lastName.trim().charAt(0) || '?'}`.toUpperCase(); }
  get canDeleteAccount(): boolean { return this.deleteConfirmation.trim().toUpperCase() === 'DELETE'; }
  get normalizedUsername(): string { return this.username.trim().toLowerCase(); }
  get isUsernameValid(): boolean { return /^[a-z0-9_.]{3,30}$/.test(this.normalizedUsername); }
  get usernameCooldownMessage(): string {
    const updatedAt = this.session.currentUser()?.usernameUpdatedAt;
    if (!updatedAt) return '';
    const previous = new Date(updatedAt);
    if (Number.isNaN(previous.getTime())) return '';
    const nextChange = new Date(previous.getTime() + 30 * 24 * 60 * 60 * 1000);
    if (Date.now() >= nextChange.getTime()) return '';
    return this.i18n.t('account.usernameCooldown', { date: this.i18n.formatDate(nextChange.toISOString().slice(0, 10)) });
  }
  get isUsernameChangeLocked(): boolean { return Boolean(this.usernameCooldownMessage); }
  get profileWizardSteps(): string[] { return this.i18n.value<string[]>('account.profileWizard.steps') ?? []; }
  get diplomaOptions(): string[] { return this.i18n.value<string[]>('account.profileWizard.diplomas') ?? []; }
  get selectedDiplomas(): string[] { return this.splitList(this.profileWizard.diplomas); }
  get cleanDiplomaDraft(): string { return this.diplomaDraft.trim(); }
  get diplomaSuggestions(): string[] {
    const selected = new Set([...this.selectedDiplomas, this.cleanDiplomaDraft].filter(Boolean).map(value => value.toLowerCase()));
    const available = this.diplomaOptions.filter(diploma => !selected.has(diploma.toLowerCase()));
    if (available.length <= 3) return available;

    return Array.from({ length: Math.min(3, available.length) }, (_item, index) => available[(this.diplomaSuggestionOffset + index) % available.length]);
  }
  get profileWizardProgress(): number { return ((this.profileWizardStep + 1) / Math.max(1, this.profileWizardSteps.length)) * 100; }
  chips(field: keyof Omit<ProfileWizardAnswers, 'diplomas'>): string[] {
    const selected = new Set(this.splitList(this.profileWizard[field]).map(value => value.toLowerCase()));
    const available = (this.i18n.value<string[]>(`account.profileWizard.chips.${field}`) ?? []).filter(chip => !selected.has(this.cleanChipValue(chip).toLowerCase()));
    if (available.length <= 3) return available;

    const offset = this.chipSuggestionOffsets[field] % available.length;
    return Array.from({ length: Math.min(3, available.length) }, (_item, index) => available[(offset + index) % available.length]);
  }
  async save(): Promise<void> {
    if (this.isSavingProfile) return;
    this.username = this.normalizedUsername;
    if (!this.isUsernameValid) {
      this.uploadStatusKey = 'account.usernameInvalid';
      return;
    }

    this.isSavingProfile = true;
    try {
      this.normalizeProfileWizard();
      await this.session.updateProfile({ username: this.normalizedUsername, firstName: this.firstName, lastName: this.lastName, email: this.canEditEmail ? this.email : undefined, avatar: this.avatar, avatarImportEnabled: this.avatarImportEnabled, profileSummary: this.profileSummary, profileWizard: this.profileWizard });
      this.uploadStatusKey = 'account.profileSaved';
      if (!this.session.needsRequiredProfileCompletion()) this.dismissProfileCompletionModal();
    } catch (error) {
      console.warn('Profile save failed.', error);
      this.uploadStatusKey = 'account.profileSaveFailed';
    } finally {
      this.isSavingProfile = false;
      this.isEditingProfileSummary = false;
    }
  }
  onProfileSummaryInput(): void { this.isEditingProfileSummary = true; }
  dismissProfileCompletionModal(): void { this.profileCompletionModalDismissed = true; this.showProfileCompletionModal = false; }
  async exportPrivacyData(): Promise<void> {
    if (this.isExportingPrivacyData) return;
    this.isExportingPrivacyData = true;
    try {
      await this.session.exportPrivacyData();
      this.uploadStatusKey = 'account.privacy.exportRequested';
    } catch (error) {
      this.uploadStatusKey = 'account.privacy.exportFailed';
    } finally {
      this.isExportingPrivacyData = false;
    }
  }
  async deleteAccount(): Promise<void> {
    if (!this.canDeleteAccount || this.isDeletingAccount) return;
    this.isDeletingAccount = true;
    try {
      await this.session.deleteAccountForPrivacy();
    } catch (error) {
      this.uploadStatusKey = 'account.privacy.deleteFailed';
      this.isDeletingAccount = false;
    }
  }
  openProfileWizard(): void { this.showProfileWizard = true; this.profileWizardStep = 0; }
  closeProfileWizard(): void { if (!this.isGeneratingProfileSummary) this.forceCloseProfileWizard(); }
  nextProfileWizardStep(): void {
    if (this.isGeneratingProfileSummary) return;
    this.profileWizardStep = Math.min(this.profileWizardSteps.length - 1, this.profileWizardStep + 1);
  }
  previousProfileWizardStep(): void {
    if (this.isGeneratingProfileSummary) return;
    this.profileWizardStep = Math.max(0, this.profileWizardStep - 1);
  }
  addDiploma(value: string): void {
    if (this.isGeneratingProfileSummary) return;

    const cleanValue = value.trim();
    if (!cleanValue) return;

    const diplomas = this.selectedDiplomas;
    if (diplomas.some(diploma => diploma.toLowerCase() === cleanValue.toLowerCase())) return;

    this.profileWizard.diplomas = [...diplomas, cleanValue].join(', ');
    this.diplomaDraft = '';
    this.diplomaSuggestionOffset += 1;
  }
  addDraftDiploma(): void {
    if (this.isGeneratingProfileSummary) return;

    this.addDiploma(this.diplomaDraft);
  }
  removeDiploma(value: string): void {
    if (this.isGeneratingProfileSummary) return;

    this.profileWizard.diplomas = this.selectedDiplomas.filter(diploma => diploma !== value).join(', ');
  }
  onSelectedDiplomaClick(event: MouseEvent, value: string): void {
    if (this.isGeneratingProfileSummary) return;

    const target = event.target as HTMLElement | null;
    if (target?.closest('.selected-chip-remove')) this.removeDiploma(value);
  }
  addWizardChip(field: keyof ProfileWizardAnswers, value: string): void {
    if (this.isGeneratingProfileSummary) return;

    const current = this.profileWizard[field].trim();
    const cleanValue = this.cleanChipValue(value);
    if (!cleanValue) return;

    const values = this.splitList(current);
    if (values.some(item => item.toLowerCase() === cleanValue.toLowerCase())) return;

    this.profileWizard[field] = [...values, cleanValue].join(', ');
    if (field !== 'diplomas') this.chipSuggestionOffsets[field] += 1;
  }
  async generateProfileSummary(): Promise<void> {
    if (this.isGeneratingProfileSummary) return;

    this.isGeneratingProfileSummary = true;
    this.normalizeProfileWizard();
    const answers = this.profileWizard;
    this.profileSummary = `${this.firstName || 'The candidate'} ${this.lastName || ''} is a PhD applicant with academic training in ${answers.diplomas || 'a relevant scientific field'}. They are especially motivated by ${answers.favoriteTopics || 'ambitious research questions'} and prefer research environments where ${answers.targetEnvironment || 'rigour, mentorship and collaboration are valued'}. Their strongest methods and skills include ${answers.methods || 'analytical thinking, structured experimentation and scientific communication'}. They are intentionally looking for projects aligned with ${answers.values || 'impactful, ethical and intellectually demanding work'}, while avoiding roles dominated by ${answers.dislikedTasks || 'poorly defined tasks without research ownership'}. Notable achievements include ${answers.achievements || 'consistent academic progress and readiness to contribute to a research team'}. Their motivation for doctoral study is ${answers.motivation || 'to develop deep expertise, publish meaningful work and grow into an independent researcher'}.${answers.extraNotes ? ` Additional context to respect when matching opportunities: ${answers.extraNotes}.` : ''}`.replace(/\s+/g, ' ').trim();
    try {
      await this.session.updateProfile({ firstName: this.firstName, lastName: this.lastName, profileSummary: this.profileSummary, profileWizard: this.profileWizard });
      this.uploadStatusKey = 'account.profileSaved';
      this.forceCloseProfileWizard();
    } catch (error) {
      this.uploadStatusKey = 'account.profileSaveFailed';
    } finally {
      this.isGeneratingProfileSummary = false;
    }
  }
  async onAvatarSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement; const file = input.files?.[0]; if (!file) return;
    if (this.isUploadingPicture) { input.value = ''; return; }
    if (!file.type.startsWith('image/')) { this.uploadStatusKey = 'account.selectImageFile'; input.value = ''; return; }
    this.isUploadingPicture = true;
    this.uploadStatusKey = 'account.pictureUploading';
    this.clearPendingPictureDeletion();
    this.setLocalAvatarPreview(file);
    try {
      await this.session.updateProfilePicture(file);
      const user = this.session.currentUser();
      this.avatar = user?.avatar ?? this.avatar;
      this.uploadStatusKey = 'account.pictureUpdated';
    } catch (error) {
      this.uploadStatusKey = 'account.pictureUploadFailed';
    } finally {
      this.isUploadingPicture = false;
      this.revokeLocalAvatarPreview();
      input.value = '';
    }
  }

  async removeProfilePicture(): Promise<void> {
    if (this.isUploadingPicture || this.isDeletingPicture || !this.avatar) return;
    const avatarUrlRequestedForDeletion = this.avatar;
    this.isDeletingPicture = true;
    this.uploadStatusKey = 'account.pictureDeleting';
    this.revokeLocalAvatarPreview();
    try {
      this.avatar = '';
      this.setPendingPictureDeletion(avatarUrlRequestedForDeletion);
      await this.session.deleteProfilePicture();
      this.uploadStatusKey = 'account.pictureDeleted';
    } catch {
      this.clearPendingPictureDeletion();
      this.syncFromCurrentUser();
      this.avatar = avatarUrlRequestedForDeletion;
      this.uploadStatusKey = 'account.pictureDeleteFailed';
    } finally {
      this.isDeletingPicture = false;
    }
  }

  private setLocalAvatarPreview(file: File): void {
    this.revokeLocalAvatarPreview();
    if (typeof URL === 'undefined' || !URL.createObjectURL) return;
    this.localAvatarPreviewUrl = URL.createObjectURL(file);
    this.avatar = this.localAvatarPreviewUrl;
  }

  private revokeLocalAvatarPreview(): void {
    if (!this.localAvatarPreviewUrl || typeof URL === 'undefined' || !URL.revokeObjectURL) return;
    URL.revokeObjectURL(this.localAvatarPreviewUrl);
    this.localAvatarPreviewUrl = '';
  }

  private syncPendingPictureDeletionNotice(): void {
    if (!this.pendingDeletedAvatarUrl) return;
    if (this.avatar === this.pendingDeletedAvatarUrl) {
      if (this.uploadStatusKey === 'account.pictureDeletionInProcess') this.uploadStatusKey = '';
    } else {
      this.clearPendingPictureDeletion();
      if (this.uploadStatusKey === 'account.pictureDeletionInProcess') this.uploadStatusKey = '';
    }
  }

  private setPendingPictureDeletion(avatarUrl: string): void {
    try { localStorage.setItem(this.pendingPictureDeletionStorageKey, avatarUrl); } catch { /* Ignore unavailable storage. */ }
  }

  private clearPendingPictureDeletion(): void {
    try { localStorage.removeItem(this.pendingPictureDeletionStorageKey); } catch { /* Ignore unavailable storage. */ }
  }

  private readPendingDeletedAvatarUrl(): string {
    try { return localStorage.getItem(this.pendingPictureDeletionStorageKey) ?? ''; } catch { return ''; }
  }

  private emptyProfileWizard(): ProfileWizardAnswers {
    return { diplomas: '', favoriteTopics: '', dislikedTasks: '', methods: '', values: '', targetEnvironment: '', achievements: '', motivation: '', extraNotes: '' };
  }
  private forceCloseProfileWizard(): void { this.showProfileWizard = false; }
  private normalizeProfileWizard(): void {
    this.profileWizard = { ...this.profileWizard, diplomas: this.uniqueListText(this.profileWizard.diplomas) };
  }
  private uniqueListText(value: string): string {
    return this.splitList(value).join(', ');
  }
  private splitList(value: string): string[] {
    const seen = new Set<string>();
    return value
      .split(',')
      .map(item => item.trim())
      .filter(item => {
        const normalized = item.toLowerCase();
        if (!item || seen.has(normalized)) return false;
        seen.add(normalized);
        return true;
      });
  }
  private cleanChipValue(value: string): string {
    return value.replace(/^[^\p{L}\p{N}]+\s*/u, '').trim();
  }
}
